import Link from 'next/link';
import React from 'react';
import styled from 'styled-components';

import { Image } from 'shared/components/Image';
import { LocalizedCurrency } from 'shared/components/LocalizedCurrency';
import { variableFontRegular } from 'styles/theme/text';
import { buildPtLinkUrl } from 'homepage/utils/buildPtLinkUrl';
import {
  VERTICAL_LIST_CAROUSEL_PRICE_LIST_SECTION,
  VERTICAL_LIST_CAROUSEL_RANK_LIST_SECTION,
} from 'homepage/enums/contentConstants';

interface IRankedOrPriceListCard {
  data?: any;
  isHomepage?: boolean;
  isModuleInViewport?: boolean;
  onClick?: () => void;
  position?: number;
  type?: string;
}

export const RankedOrPriceListCard = ({
  data,
  isHomepage = false,
  isModuleInViewport = false,
  onClick,
  position,
  type,
}: IRankedOrPriceListCard) => {
  const linkUrl = buildPtLinkUrl(data);
  const isUnderRetail =
    !!data?.retail_price_cents &&
    !!data?.lowest_price_cents &&
    data?.lowest_price_cents < data?.retail_price_cents;

  const showRetailPrice = !!data?.retail_price_cents && !data.lowest_price_cents;

  return (
    <Link
      href={linkUrl}
      prefetch={false}
      key={`${type}-${data?.slug as string}`}
      passHref
      legacyBehavior
    >
      <RankedOrPriceListCard.Container
        data-qa="masonry_list_item_link"
        $isPriceList={type === VERTICAL_LIST_CAROUSEL_PRICE_LIST_SECTION}
        key={`${type}-${position}`}
        onClick={onClick}
      >
        {type === VERTICAL_LIST_CAROUSEL_RANK_LIST_SECTION && (
          <RankedOrPriceListCard.Index>{position}</RankedOrPriceListCard.Index>
        )}
        <RankedOrPriceListCard.Image
          alt={[data?.brand, data?.color, data?.category].filter(Boolean).join(' ')}
          data-qa="masonry_list_item_item"
          disableLazyLoad={isModuleInViewport}
          key={data?.id}
          sizes="(max-width: 1280px) 75px, (min-width: 1281px) 100px"
          src={
            isModuleInViewport || !isHomepage
              ? data?.image_url || data?.gridPictureUrl || data?.pictureUrl
              : `missing.png`
          }
        />
        {type === VERTICAL_LIST_CAROUSEL_PRICE_LIST_SECTION && (
          <RankedOrPriceListCard.PriceContainer>
            <RankedOrPriceListCard.Price>
              {isUnderRetail && (
                <RankedOrPriceListCard.LineThroughCurrency price={data.retail_price_cents} />
              )}
              {showRetailPrice && <LocalizedCurrency price={data?.retail_price_cents} />}
              {!isUnderRetail && !showRetailPrice && (
                <LocalizedCurrency price={data.lowest_price_cents} />
              )}
            </RankedOrPriceListCard.Price>
            {isUnderRetail && (
              <RankedOrPriceListCard.PriceUnderRetail>
                <LocalizedCurrency price={data.lowest_price_cents} />
              </RankedOrPriceListCard.PriceUnderRetail>
            )}
          </RankedOrPriceListCard.PriceContainer>
        )}
      </RankedOrPriceListCard.Container>
    </Link>
  );
};

RankedOrPriceListCard.Container = styled.a<{ $isPriceList: boolean }>`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 90px;
  ${({ $isPriceList }) => $isPriceList && `flex-direction: column`};
  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    flex-direction: row;
  }
`;

RankedOrPriceListCard.Image = styled(Image)`
  height: 64px;
  width: 64px !important;
  min-width: 64px;
  margin-bottom: 8px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    height: auto;
    width: 33.3% !important;
    min-width: 33.3%;
    margin-bottom: 0;
  }
`;

RankedOrPriceListCard.Index = styled.h3`
  font-weight: 400;
  margin-right: 24px;
  ${variableFontRegular({ size: '18px', height: '15px', spacing: '-0.5px' })};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin-right: 38px;
  }
`;

RankedOrPriceListCard.PriceContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-start;
  margin-bottom: 24px;
  width: 40px;
  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    flex-direction: column;
    margin-left: 24px;
    margin-bottom: 0;
  }
`;

RankedOrPriceListCard.Price = styled.h3`
  font-weight: 400;
  ${variableFontRegular({ size: '11.5px', height: '11px' })};
`;

RankedOrPriceListCard.PriceUnderRetail = styled(RankedOrPriceListCard.Price)`
  color: ${({ theme }) => theme.colors.white};
`;

RankedOrPriceListCard.LineThroughCurrency = styled(LocalizedCurrency)`
  text-decoration: line-through;
  margin-right: 1px;
`;
